/**
 * Map Filter Panel - Campaign and Flyer Type filters
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ALL_CAMPAIGNS_ID, isAllCampaignsMode } from "../../api/map/campaignScope";
import "./MapFilterPanel.css";

export interface Campaign {
    id: string;
    name: string;
}

export interface FlyerType {
    id: string;
    name: string;
}

export type MapMode = "flyer" | "poster";

export type HouseholdStats =
    | { mode: "all"; count: number; total: number | null }
    | { mode: "deliveredSince"; count: number; total: number | null; since: string }
    | { mode: "deliveredBetween"; count: number; total: number | null; since: string; until: string }
    | { mode: "notDeliveredSince"; count: number; total: number | null; since: string };

export interface MapFilterPanelProps {
    campaigns?: Campaign[];
    flyerTypes?: FlyerType[];
    selectedCampaignId?: string;
    selectedFlyerTypeId?: string | null;
    onCampaignChange?: (campaignId: string) => void;
    onFlyerTypeChange?: (flyerTypeId: string | null) => void;
    allCampaignsShowUndelivered?: boolean;
    onAllCampaignsShowUndeliveredChange?: (show: boolean) => void;
    /**
     * Campaign/flyer view: whether never-delivered address points are drawn too. The
     * campaign-mode counterpart of `allCampaignsShowUndelivered`, minus the date filters —
     * those belong to the all-campaigns overview, which spans every campaign at once.
     */
    campaignShowUndelivered?: boolean;
    onCampaignShowUndeliveredChange?: (show: boolean) => void;
    /** True while the zoom level forces the delivery overview, whatever the checkbox says. */
    campaignUndeliveredForcedOff?: boolean;
    /** ISO date (YYYY-MM-DD). Narrows the undelivered view to addresses never delivered, or last delivered before this date. */
    notDeliveredSinceDate?: string | null;
    onNotDeliveredSinceDateChange?: (date: string | null) => void;
    /** ISO date (YYYY-MM-DD). Opposite of notDeliveredSinceDate: only addresses last delivered on/after this date. */
    deliveredSinceDate?: string | null;
    onDeliveredSinceDateChange?: (date: string | null) => void;
    /** ISO date (YYYY-MM-DD). Optional upper bound for deliveredSinceDate — turns it into a closed window. */
    deliveredUntilDate?: string | null;
    onDeliveredUntilDateChange?: (date: string | null) => void;
    /**
     * Reached-households summary for the current scope: `total` is only known once undelivered
     * addresses are also loaded (checkbox on) — null otherwise, so only `count` is shown.
     * `mode` follows whichever date filter is active, so the number always matches what the
     * map currently shows rather than the all-time total.
     */
    householdStats?: HouseholdStats | null;
    isLoading?: boolean;
    mapMode?: MapMode;
    onMapModeChange?: (mode: MapMode) => void;
}

const formatCount = (n: number): string => new Intl.NumberFormat("sv-SE").format(n);

const toDateInputValue = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const daysAgo = (days: number): string => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return toDateInputValue(d);
};

const monthsAgo = (months: number): string => {
    const d = new Date();
    d.setMonth(d.getMonth() - months);
    return toDateInputValue(d);
};

const HOUSEHOLD_STATS_KEY: Record<HouseholdStats["mode"], string> = {
    all: "householdsReached",
    deliveredSince: "householdsReachedSince",
    deliveredBetween: "householdsReachedBetween",
    notDeliveredSince: "householdsNotReachedSince"
};

export const MapFilterPanel = ({
    campaigns = [],
    flyerTypes = [],
    selectedCampaignId,
    selectedFlyerTypeId,
    onCampaignChange,
    onFlyerTypeChange,
    allCampaignsShowUndelivered = false,
    onAllCampaignsShowUndeliveredChange,
    campaignShowUndelivered = true,
    onCampaignShowUndeliveredChange,
    campaignUndeliveredForcedOff = false,
    notDeliveredSinceDate = null,
    onNotDeliveredSinceDateChange,
    deliveredSinceDate = null,
    onDeliveredSinceDateChange,
    deliveredUntilDate = null,
    onDeliveredUntilDateChange,
    householdStats = null,
    isLoading = false,
    mapMode = "flyer",
    onMapModeChange
}: MapFilterPanelProps): JSX.Element => {
    const { t } = useTranslation();
    const showFlyerFilter = !!selectedCampaignId && !isAllCampaignsMode(selectedCampaignId);
    const showAllCampaignsOptions = isAllCampaignsMode(selectedCampaignId);

    // Progressive disclosure: the "until" field only appears once the user asks for it (or
    // already has a value), keeping the common single-date case as compact as before.
    const [showUntilField, setShowUntilField] = useState(false);
    useEffect(() => {
        if (!deliveredSinceDate) {
            setShowUntilField(false);
        }
    }, [deliveredSinceDate]);

    const handleCampaignChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const campaignId = e.target.value;
        if (onCampaignChange) {
            // Always call onCampaignChange, even if campaignId is empty (to clear selection)
            onCampaignChange(campaignId || "");
        }
    };

    const handleFlyerTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const flyerTypeId = e.target.value || null;
        if (onFlyerTypeChange) {
            onFlyerTypeChange(flyerTypeId);
        }
    };

    const handleClearFlyerFilter = () => {
        if (onFlyerTypeChange) {
            onFlyerTypeChange(null);
        }
    };

    return (
        <div className="map-filter-panel" role="region" aria-label={t("map.filter.title")}>
            <h3 className="map-filter-panel__title">{t("map.filter.title")}</h3>

            <div className="map-filter-panel__section">
                <label htmlFor="campaign-filter" className="map-filter-panel__label">
                    {t("map.filter.campaign")}
                </label>
                <select
                    id="campaign-filter"
                    className="map-filter-panel__select"
                    value={selectedCampaignId ?? ""}
                    onChange={handleCampaignChange}
                    disabled={isLoading || campaigns.length === 0}
                    aria-label={t("map.filter.campaign")}
                >
                    {campaigns.length === 0 ? (
                        <option value="">{t("map.filter.noCampaigns")}</option>
                    ) : (
                        <>
                            <option value="">{t("map.filter.selectCampaign")}</option>
                            <option value={ALL_CAMPAIGNS_ID}>{t("map.filter.allCampaigns")}</option>
                            {campaigns.map((campaign) => (
                                <option key={campaign.id} value={campaign.id}>
                                    {campaign.name}
                                </option>
                            ))}
                        </>
                    )}
                </select>
            </div>

            {showFlyerFilter && (
                <div className="map-filter-panel__section">
                    <div className="map-filter-panel__label-row">
                        <label htmlFor="flyer-filter" className="map-filter-panel__label">
                            {t("map.filter.flyerType")}
                        </label>
                        {selectedFlyerTypeId && (
                            <button
                                type="button"
                                className="map-filter-panel__clear"
                                onClick={handleClearFlyerFilter}
                                aria-label={t("map.filter.clearFlyer")}
                            >
                                {t("map.filter.clear")}
                            </button>
                        )}
                    </div>
                    <select
                        id="flyer-filter"
                        className="map-filter-panel__select"
                        value={selectedFlyerTypeId ?? ""}
                        onChange={handleFlyerTypeChange}
                        disabled={isLoading || flyerTypes.length === 0}
                        aria-label={t("map.filter.flyerType")}
                    >
                        {flyerTypes.length === 0 ? (
                            <option value="">{t("map.filter.noFlyerTypes")}</option>
                        ) : (
                            <>
                                <option value="">{t("map.filter.allFlyers")}</option>
                                {flyerTypes
                                    .filter((flyer): flyer is FlyerType => flyer !== null && flyer !== undefined)
                                    .map((flyer) => (
                                        <option key={flyer.id} value={flyer.id}>
                                            {flyer.name}
                                        </option>
                                    ))}
                            </>
                        )}
                    </select>
                </div>
            )}

            {householdStats && (
                <div className="map-filter-panel__section map-filter-panel__stats">
                    <span className="map-filter-panel__stats-icon" aria-hidden="true">
                        🏠
                    </span>
                    <span className="map-filter-panel__stats-text">
                        {t(
                            `map.filter.${HOUSEHOLD_STATS_KEY[householdStats.mode]}${
                                householdStats.total != null ? "OfTotal" : ""
                            }`,
                            {
                                countText: formatCount(householdStats.count),
                                total: householdStats.total != null ? formatCount(householdStats.total) : undefined,
                                percent:
                                    householdStats.total != null
                                        ? Math.round((householdStats.count / Math.max(1, householdStats.total)) * 100)
                                        : undefined,
                                since: householdStats.mode !== "all" ? householdStats.since : undefined,
                                until: householdStats.mode === "deliveredBetween" ? householdStats.until : undefined
                            }
                        )}
                    </span>
                </div>
            )}

            {showAllCampaignsOptions && onAllCampaignsShowUndeliveredChange && (
                <div className="map-filter-panel__section">
                    <label className="map-filter-panel__checkbox-label">
                        <input
                            type="checkbox"
                            className="map-filter-panel__checkbox"
                            checked={allCampaignsShowUndelivered}
                            onChange={(e) => onAllCampaignsShowUndeliveredChange(e.target.checked)}
                            disabled={isLoading}
                        />
                        <span>{t("map.filter.showUndeliveredAddresses")}</span>
                    </label>
                    {allCampaignsShowUndelivered && onNotDeliveredSinceDateChange && (
                        <div className="map-filter-panel__subfield">
                            <div className="map-filter-panel__label-row">
                                <label htmlFor="not-delivered-since-filter" className="map-filter-panel__label">
                                    {t("map.filter.notDeliveredSince")}
                                </label>
                                {notDeliveredSinceDate && (
                                    <button
                                        type="button"
                                        className="map-filter-panel__clear"
                                        onClick={() => onNotDeliveredSinceDateChange(null)}
                                        aria-label={t("map.filter.clear")}
                                    >
                                        {t("map.filter.clear")}
                                    </button>
                                )}
                            </div>
                            <input
                                id="not-delivered-since-filter"
                                type="date"
                                className="map-filter-panel__date-input"
                                value={notDeliveredSinceDate ?? ""}
                                onChange={(e) => onNotDeliveredSinceDateChange(e.target.value || null)}
                                disabled={isLoading}
                                aria-label={t("map.filter.notDeliveredSince")}
                            />
                            <div className="map-filter-panel__presets">
                                <button
                                    type="button"
                                    className="map-filter-panel__preset-btn"
                                    onClick={() => onNotDeliveredSinceDateChange(daysAgo(14))}
                                    disabled={isLoading}
                                >
                                    {t("map.filter.presetTwoWeeks")}
                                </button>
                                <button
                                    type="button"
                                    className="map-filter-panel__preset-btn"
                                    onClick={() => onNotDeliveredSinceDateChange(monthsAgo(1))}
                                    disabled={isLoading}
                                >
                                    {t("map.filter.presetOneMonth")}
                                </button>
                            </div>
                        </div>
                    )}
                    {onDeliveredSinceDateChange && (
                        <div className="map-filter-panel__subfield">
                            <div className="map-filter-panel__label-row">
                                <label htmlFor="delivered-since-filter" className="map-filter-panel__label">
                                    {t("map.filter.deliveredSince")}
                                </label>
                                {deliveredSinceDate && (
                                    <button
                                        type="button"
                                        className="map-filter-panel__clear"
                                        onClick={() => onDeliveredSinceDateChange(null)}
                                        aria-label={t("map.filter.clear")}
                                    >
                                        {t("map.filter.clear")}
                                    </button>
                                )}
                            </div>
                            <input
                                id="delivered-since-filter"
                                type="date"
                                className="map-filter-panel__date-input"
                                value={deliveredSinceDate ?? ""}
                                onChange={(e) => onDeliveredSinceDateChange(e.target.value || null)}
                                disabled={isLoading}
                                aria-label={t("map.filter.deliveredSince")}
                            />
                            {deliveredSinceDate && onDeliveredUntilDateChange && (
                                deliveredUntilDate || showUntilField ? (
                                    <div className="map-filter-panel__until-row">
                                        <label
                                            htmlFor="delivered-until-filter"
                                            className="map-filter-panel__label map-filter-panel__label--inline"
                                        >
                                            {t("map.filter.deliveredUntil")}
                                        </label>
                                        <input
                                            id="delivered-until-filter"
                                            type="date"
                                            className="map-filter-panel__date-input map-filter-panel__date-input--inline"
                                            min={deliveredSinceDate}
                                            value={deliveredUntilDate ?? ""}
                                            onChange={(e) => onDeliveredUntilDateChange(e.target.value || null)}
                                            disabled={isLoading}
                                            aria-label={t("map.filter.deliveredUntil")}
                                        />
                                        <button
                                            type="button"
                                            className="map-filter-panel__clear"
                                            onClick={() => {
                                                onDeliveredUntilDateChange(null);
                                                setShowUntilField(false);
                                            }}
                                            aria-label={t("map.filter.clear")}
                                        >
                                            {t("map.filter.clear")}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className="map-filter-panel__add-until"
                                        onClick={() => setShowUntilField(true)}
                                    >
                                        {t("map.filter.addUntilDate")}
                                    </button>
                                )
                            )}
                        </div>
                    )}
                </div>
            )}

            {showFlyerFilter && onCampaignShowUndeliveredChange && (
                <div className="map-filter-panel__section">
                    <label className="map-filter-panel__checkbox-label">
                        <input
                            type="checkbox"
                            className="map-filter-panel__checkbox"
                            checked={campaignShowUndelivered && !campaignUndeliveredForcedOff}
                            onChange={(e) => onCampaignShowUndeliveredChange(e.target.checked)}
                            disabled={isLoading || campaignUndeliveredForcedOff}
                        />
                        <span>{t("map.filter.showUndeliveredAddresses")}</span>
                    </label>
                    {campaignUndeliveredForcedOff && (
                        <p className="map-filter-panel__hint">{t("map.filter.undeliveredNeedsZoom")}</p>
                    )}
                </div>
            )}

            {onMapModeChange && !isAllCampaignsMode(selectedCampaignId) && (
                <div className="map-filter-panel__section map-filter-panel__section--mode">
                    <button
                        type="button"
                        className={`map-filter-panel__mode-btn${mapMode === "poster" ? " map-filter-panel__mode-btn--active" : ""}`}
                        onClick={() => onMapModeChange(mapMode === "poster" ? "flyer" : "poster")}
                        aria-pressed={mapMode === "poster"}
                    >
                        {mapMode === "poster" ? "🪧 Affischläge" : "📄 Flygbladsläge"}
                    </button>
                </div>
            )}

            {isLoading && (
                <div className="map-filter-panel__loading" aria-live="polite">
                    {t("map.filter.loading")}
                </div>
            )}
        </div>
    );
};